import axiosConn from '../axiosConn';

export const SHARE_FUNNEL_EVENT_NAMES = Object.freeze({
  LANDING_VIEWED: 'localops_share_landing_viewed',
  ACCEPT_CLICKED: 'localops_share_accept_clicked',
  CONTINUE_STARTED: 'localops_share_continue_started',
  PLAYERMAP_ARRIVED: 'localops_share_playermap_arrived',
  TASK_SHEET_OPENED: 'localops_share_task_sheet_opened',
  TASK_ACCEPT_CLICKED: 'localops_share_task_accept_clicked',
  TASK_ACCEPT_SUCCEEDED: 'localops_share_task_accept_succeeded',
  TASK_ACCEPT_FAILED: 'localops_share_task_accept_failed',
});

const STORAGE_KEY = 'jg:localopsShareFunnel:v1';
const SENT_KEY = 'jg:localopsShareFunnelSent:v1';
const SAFE_PARAM_KEYS = [
  'src',
  'source',
  'utm_source',
  'utm_campaign',
  'intent',
  'regionSlug',
  'marketSlug',
];

const clean = (value, max = 256) =>
  String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, max);

const getStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage || null;
  } catch (err) {
    return null;
  }
};

const randomId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sf_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

const readJson = (key, fallback) => {
  const storage = getStorage();
  if (!storage) return fallback;
  try {
    const parsed = JSON.parse(storage.getItem(key) || '');
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch (err) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // ignore storage failures
  }
};

export const parseSharePath = (value) => {
  const raw = clean(value, 512);
  if (!raw) return null;
  let path = raw;
  if (/^https?:\/\//i.test(raw)) {
    try {
      path = new URL(raw).pathname;
    } catch (err) {
      return null;
    }
  }
  const match = path.match(/^\/share\/(task|offering)\/([^/?#]+)/i);
  if (!match) return null;
  return {
    shareType: match[1].toLowerCase(),
    shareId: decodeURIComponent(match[2]),
    share: `/share/${match[1].toLowerCase()}/${decodeURIComponent(match[2])}`,
  };
};

export const buildShareFunnelContext = ({
  share,
  shareType,
  shareId,
  queryParams,
} = {}) => {
  const parsed = parseSharePath(
    share ||
      (shareType && shareId
        ? `/share/${shareType}/${encodeURIComponent(String(shareId))}`
        : ''),
  );
  if (!parsed) return null;
  const existing = readJson(STORAGE_KEY, {});
  const shareFunnelId =
    clean(queryParams?.get?.('shareFunnelId'), 256) ||
    clean(existing.shareFunnelId, 256) ||
    randomId();
  const context = {
    shareFunnelId,
    share: parsed.share,
    shareType: parsed.shareType,
    shareId: parsed.shareId,
  };
  SAFE_PARAM_KEYS.forEach((key) => {
    const value = clean(queryParams?.get?.(key) || existing[key], 120);
    if (value) context[key] = value;
  });
  writeJson(STORAGE_KEY, context);
  return context;
};

export const readShareFunnelContextFromSearch = (search = '') => {
  const queryParams = new URLSearchParams(search || '');
  const share = queryParams.get('share');
  const shareFunnelId = clean(queryParams.get('shareFunnelId'), 256);
  const parsed = parseSharePath(share);
  if (!parsed || !shareFunnelId) return null;
  return buildShareFunnelContext({
    share: parsed.share,
    queryParams,
  });
};

export const appendShareFunnelParams = (url, context) => {
  if (!url || !context?.shareFunnelId || !context?.share) return url;
  try {
    const parsed = new URL(url, 'https://www.joegalaxy.net');
    parsed.searchParams.set('share', context.share);
    parsed.searchParams.set('shareFunnelId', context.shareFunnelId);
    parsed.searchParams.set('shareType', context.shareType);
    parsed.searchParams.set('shareId', context.shareId);
    SAFE_PARAM_KEYS.forEach((key) => {
      if (context[key]) parsed.searchParams.set(key, context[key]);
    });
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (err) {
    return url;
  }
};

const buildEventId = ({ eventName, context, taskId, failureReason }) =>
  [
    'share-funnel',
    eventName,
    context?.shareFunnelId,
    context?.shareType,
    context?.shareId,
    taskId || '',
    failureReason || '',
  ]
    .filter(Boolean)
    .join(':');

export const recordShareFunnelEvent = async (
  eventName,
  context,
  extra = {},
) => {
  if (!eventName || !context?.shareFunnelId || !context?.share) return null;
  const sent = readJson(SENT_KEY, {});
  const eventId =
    extra.eventId ||
    buildEventId({
      eventName,
      context,
      taskId: extra.taskId,
      failureReason: extra.failureReason,
    });
  if (extra.dedupe !== false && sent[eventId]) return null;
  sent[eventId] = Date.now();
  writeJson(SENT_KEY, sent);
  const body = {
    eventName,
    eventId,
    shareFunnelId: context.shareFunnelId,
    share: context.share,
    shareType: context.shareType,
    shareId: context.shareId,
    source: context.source,
    src: context.src,
    utm_source: context.utm_source,
    utm_campaign: context.utm_campaign,
    intent: context.intent,
    regionSlug: context.regionSlug,
    marketSlug: context.marketSlug,
    taskId: extra.taskId,
    stage: extra.stage,
    failureReason: extra.failureReason,
  };
  try {
    return await axiosConn.post('local-ops/share-funnel-events', body);
  } catch (err) {
    return null;
  }
};
